import { useCallback, useEffect, useState } from 'react'

import { Box, Button, type IllustrationType, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0TagStatus } from '@factorialco/f0-react'
import { ArrowRight } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { PayrollRunInfoPayrollRunFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import {
  PayrollRunStatus,
  getNextPayrollRunStatus,
  getPayrollRunStatusText,
  getPayrollRunStatusVariant,
} from 'modules/compensations/lib/payrollRunStatus'
import { useIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

type TransitionSource = 'export' | 'sync' | 'sendToBK'
type TransitionedStatuses = Exclude<PayrollRunStatus, typeof PayrollRunStatus.OPEN>

// TODO: We could potentially move this stuff to a centralized configuration
const STATUS_ILLUSTRATION = {
  [PayrollRunStatus.IN_PREPARATION]: 'permissions',
  [PayrollRunStatus.CALCULATION]: 'calculator',
  [PayrollRunStatus.IN_REVIEW]: 'task-list-search',
  [PayrollRunStatus.COMPLETED]: 'alert',
} satisfies Record<TransitionedStatuses, IllustrationType>

/**
 * Get the appropriate illustration for a status
 */
const getStatusIllustration = (status: TransitionedStatuses): IllustrationType => {
  return STATUS_ILLUSTRATION[status]
}

/**
 * Get the description for a specific transition source
 */
const getTransitionDescription = (source: TransitionSource): string => {
  if (source === 'export') {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.export'
    )
  } else if (source === 'sync') {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.sync'
    )
  } else if (source === 'sendToBK') {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.sendToBK'
    )
  }

  throw new Error(`Invalid transition source: ${source}`)
}

/**
 * Determine the appropriate transition source based on current and target status
 */
const determineTransitionSource = (
  isSyncEnabled: boolean,
  hasProfessionals?: boolean
): TransitionSource => {
  return isSyncEnabled ? 'sync' : hasProfessionals ? 'sendToBK' : 'export'
}

const getDescriptionText = (
  targetStatus: TransitionedStatuses,
  isSyncEnabled: boolean,
  hasProfessionals?: boolean
): string => {
  if (targetStatus === PayrollRunStatus.IN_REVIEW) {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.toInReview'
    )
  }

  const source = determineTransitionSource(isSyncEnabled, hasProfessionals)
  return getTransitionDescription(source)
}

const useCalculationToReviewModal = (payrollRunId: number | undefined, status?: string) => {
  const storageKey = `${LOCAL_STORAGE_KEYS.compensationsCalculationToReviewModalShown}_${payrollRunId}`

  const [storedValue, setValue] = useLocalStorage(storageKey, false)

  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (payrollRunId && status === PayrollRunStatus.IN_REVIEW && !storedValue) {
      setShowModal(true)
    }
  }, [payrollRunId, status, storedValue])

  const handleClose = useCallback(() => {
    setShowModal(false)
    setValue(true)
  }, [setValue])

  return { showModal, handleClose }
}

type PayrollRunStatusTransitionModalProps = {
  payrollRun: PayrollRunInfoPayrollRunFragment
  targetStatus: TransitionedStatuses
  hasProfessionals?: boolean
  onClose: () => void
}

const PayrollRunStatusTransitionModal = ({
  payrollRun,
  targetStatus,
  hasProfessionals,
  onClose,
}: PayrollRunStatusTransitionModalProps) => {
  const { syncEnabled, marketplaceIntegration } = useIntegrationsSyncContext()
  // TODO: we should abstract this somewhere else
  const isSyncEnabled = Boolean(syncEnabled && marketplaceIntegration)

  const period = stringToDate(payrollRun.startsOn, 'backend_datetime').format('MMMM YYYY')

  const title = i18n.t(
    'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.title',
    {
      period,
    }
  )

  let prevStatus = payrollRun.status
  if (targetStatus === prevStatus) {
    if (targetStatus === PayrollRunStatus.IN_REVIEW) {
      prevStatus = PayrollRunStatus.CALCULATION
    } else if (targetStatus === PayrollRunStatus.CALCULATION) {
      prevStatus = PayrollRunStatus.IN_PREPARATION
    }
  }

  const nextStatus = targetStatus || getNextPayrollRunStatus(prevStatus)

  const currentStatusText = getPayrollRunStatusText(prevStatus)
  const nextStatusText = getPayrollRunStatusText(nextStatus)
  const currentStatusVariant = getPayrollRunStatusVariant(prevStatus)
  const nextStatusVariant = getPayrollRunStatusVariant(nextStatus)

  const descriptionText = getDescriptionText(targetStatus, isSyncEnabled, hasProfessionals)
  const illustration = getStatusIllustration(targetStatus)

  return (
    <Modal
      illustration={illustration}
      title={title}
      onClose={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {i18n.t('common.close')}
        </Button>
      }
    >
      <Box flexDirection="column" gap="s24">
        <Box alignItems="center" flexDirection="row" gap="s12">
          <F0TagStatus text={currentStatusText} variant={currentStatusVariant} />
          <F0Icon icon={ArrowRight} />
          <F0TagStatus text={nextStatusText} variant={nextStatusVariant} />
        </Box>
        <Box flexDirection="column" gap="s12">
          <Text.Normal size="300" color="textSecondary">
            {descriptionText}
          </Text.Normal>
        </Box>
      </Box>
    </Modal>
  )
}

export {
  PayrollRunStatusTransitionModal,
  getTransitionDescription,
  useCalculationToReviewModal,
  getDescriptionText,
}
export type { PayrollRunStatusTransitionModalProps }
